import React from 'react';
import { observer } from "mobx-react";
import { ShowableCart, showableCartInstance } from 'ReactApp/Ddd/Aggregates/ShowableCart';
import { priceFormatter } from 'ReactApp/Services/Formatters/PriceFormatter';
import { CommonLabelsEnum, PaymentTypeEnum, ShowableCartSourceModeEnum } from 'ReactApp/Types/enum';
import BadgeListV1 from 'ReactApp/Components/BadgeListV1/BadgeListV1';
import ReceiptRowV1 from 'ReactApp/Components/ReceiptRowV1/ReceiptRowV1';
import ReceiptRowV2 from 'ReactApp/Components/ReceiptRowV2/ReceiptRowV2';
import ReceiptRowV3 from 'ReactApp/Components/ReceiptRowV3/ReceiptRowV3';
import ReceiptRowV4 from 'ReactApp/Components/ReceiptRowV4/ReceiptRowV4';
import ReceiptRowV5 from 'ReactApp/Components/ReceiptRowV5/ReceiptRowV5';
import ReceiptRowV6 from 'ReactApp/Components/ReceiptRowV6/ReceiptRowV6';
import ReceiptRowV9 from 'ReactApp/Components/ReceiptRowV9/ReceiptRowV9';
import { deliveryTypeTransformer } from 'ReactApp/Services/TsEnum2AnyTransformers/DeliveryType';
import { paymentTypeTransformer } from 'ReactApp/Services/TsEnum2AnyTransformers/PaymentType';
import { InfoBadgeNs } from 'ReactApp/Types/InfoBadgeNs';
import LinkV1 from 'ReactApp/Components/LinkV1/LinkV1';
import CartSubmitButtonV1 from '../CartSubmitButtonV1/CartSubmitButtonV1';
import PromoCodeFormV1 from '../PromoCodeFormV1/PromoCodeFormV1';

type CartSummaryBlockV1Props = {
    showableCart: ShowableCart
}

const CartSummaryBlockV1 = observer((props: CartSummaryBlockV1Props) => {

    const isReadonlyMode: boolean = (
        props.showableCart.sourceMode 
            === 
        ShowableCartSourceModeEnum.order
    );

    const renderPaymentBlock = (): string | React.JSX.Element => {
        if (props.showableCart.order.paymentType === null) {
            return <></>;
        }

        let replacements: any[] = [];

        return (
            <div className="receipt-block-v1">
                <ReceiptRowV2 {...{
                    value: 'Способ оплаты',
                }} />
                <ReceiptRowV6 {...{
                    subvalue: paymentTypeTransformer.asLabel(
                        props.showableCart.order.paymentType, replacements
                    ),
                }} />
            </div>
        );
    }

    const renderSummaryBlock = (): string | React.JSX.Element => {

        let savingMoneyBadges: InfoBadgeNs.InfoBadge[] = [];
        if (
            props.showableCart.totalSavingMoneySum !== null
                &&
            props.showableCart.totalSavingMoneySum > 0
        ) {
            savingMoneyBadges.push({
                label: priceFormatter.format(props.showableCart.totalSavingMoneySum),
                classes: [
                    'badge-v1_filling-color-type-secondary',
                    'badge-v1_filling-type-contained',
                    'badge-v1_text-color-type-text',
                    'badge-v1_content-type-text'
                ]
            });
        }
        if (
            props.showableCart.totalDiscountPercent !== null
                &&
            props.showableCart.totalDiscountPercent > 0
        ) {
            savingMoneyBadges.push({
                label: `${props.showableCart.totalDiscountPercent} %`,
                classes: [
                    'badge-v1_filling-color-type-main',
                    'badge-v1_filling-type-contained',
                    'badge-v1_text-color-type-white',
                    'badge-v1_content-type-text',
                ],
            });
        }

        return (
            <div className="receipt-block-v1">
                {props.showableCart.totalBaseSum !== null && (
                    <ReceiptRowV2 {...{
                        value: 'Сумма',
                        subvalue: priceFormatter.format(props.showableCart.totalBaseSum)
                    }} />
                )}
                {props.showableCart.totalDiscountSum !== null && (
                    <ReceiptRowV5 {...{
                        value: 'Цена',
                        subvalue: priceFormatter.format(props.showableCart.totalDiscountSum)
                    }} />
                )}
                {savingMoneyBadges.length > 0 && (
                    <ReceiptRowV9 {...{
                        value: 'Экономия',
                        subvalue: renderBadgeList(savingMoneyBadges)
                    }} />
                )}
            </div>
        );
    }

    const renderBadgeList = (badges: InfoBadgeNs.InfoBadge[]): string | React.JSX.Element => {
        return (
            <BadgeListV1 {...{
                badges: badges
            }} />
        );
    }

    return (
        <div className="receipt-v1">

            {(
                isReadonlyMode === true 
                    && 
                showableCartInstance.promoInfo.promoCode !== null
            ) && (
                <div className="receipt-block-v1">
                    <ReceiptRowV2 {...{
                        value: 'Промо-код:',
                        subvalue: showableCartInstance.promoInfo.promoCode
                    }} />
                </div>
            )}

            {isReadonlyMode === false && (
                <PromoCodeFormV1 />
            )}

            <div className="receipt-block-v1">
                <ReceiptRowV1 {...{
                    value: 'детали заказа',
                    subvalue: (props.showableCart.order?.number !== null)
                        ? `№: ${props.showableCart.order?.formattedNumber}`
                        : undefined
                }} />
            </div>

            <div className="receipt-block-v1">
                <ReceiptRowV2 {...{
                    value: 'Товары',
                }} />
                {props.showableCart.cards.map((card, index) => {
                    if (card.cartInfo.isDeleted === true) {
                        return;
                    }
                    if (card.salePrice?.isGift === true) {
                        return (
                            <ReceiptRowV4 key={`rrv4-${index}`} {...{
                                value: card.title,
                                subvalue: renderBadgeList([
                                    {
                                        label: CommonLabelsEnum.productAsGift,
                                        classes: [
                                            'badge-v1_text-color-type-text',
                                            'badge-v1_filling-color-type-secondary',
                                            'badge-v1_filling-type-contained',
                                            'badge-v1_icon-color-type-white',
                                            'badge-v1_content-type-label',
                                        ]
                                    }
                                ])
                            }} />
                        );
                    } else {
                        return (
                            <ReceiptRowV3 key={`rrv3-${index}`} {...{
                                value: (
                                    (card.title as string)
                                        +
                                    (
                                        card.currentRealModifier !== undefined && (
                                            (` ${card.currentRealModifier.label}`)
                                        )
                                    )
                                ),
                                subvalue: `${(card.cartInfo.number as number) > 1 ? `${card.cartInfo.number} x ` : ''}${(card.salePrice !== null)
                                    ? priceFormatter.format(card.salePrice.value)
                                    : CommonLabelsEnum.productHasNoPrice}`
                            }} />
                        );
                    }
                })}
            </div> 

            {(
                props.showableCart.order.clientInfo.name !== null
                    ||
                props.showableCart.order.clientInfo.phone !== null
                    ||
                props.showableCart.order.clientInfo.email !== null
                    ||
                props.showableCart.order.comment !== null
            ) && (
                <div className="receipt-block-v1">
                    <ReceiptRowV2 {...{
                        value: 'Данные клиента',
                    }} />
                    {props.showableCart.order.clientInfo.name !== null && (
                        <ReceiptRowV6 {...{
                            value: 'ФИО',
                            subvalue: (props.showableCart.order.clientInfo.name || undefined)
                        }} />
                    )}
                    {props.showableCart.order.clientInfo.phone !== null && (
                        <ReceiptRowV6 {...{
                            value: 'Телефон',
                            subvalue: (props.showableCart.order.clientInfo.phone || undefined)
                        }} />
                    )}
                    {props.showableCart.order.clientInfo.email !== null && (
                        <ReceiptRowV6 {...{
                            value: 'Почта',
                            subvalue: (props.showableCart.order.clientInfo.email || undefined)
                        }} />
                    )}
                    {props.showableCart.order.comment !== null && (
                        <ReceiptRowV6 {...{
                            value: 'Комментарий к заказу',
                            subvalue: (props.showableCart.order.comment || undefined)
                        }} />
                    )}
                </div>
            )}

            {props.showableCart.order.deliveryType !== null && (
                <div className="receipt-block-v1">
                    <ReceiptRowV2 {...{
                        value: 'Способ доставки',
                    }} />
                    <ReceiptRowV6 {...{
                        subvalue: deliveryTypeTransformer.asLabel(props.showableCart.order.deliveryType),
                    }} />
                    {props.showableCart.order.deliveryAddress !== null && (
                        <ReceiptRowV6 {...{
                            subvalue: (props.showableCart.order.deliveryAddress || undefined)
                        }} />
                    )}
                    {props.showableCart.order.sdekPickupPoint.address !== null &&  (
                        <ReceiptRowV6 {...{
                            subvalue: (props.showableCart.order.sdekPickupPoint.address || undefined)
                        }} />
                    )}
                </div>
            )}

            {renderPaymentBlock()}

            {renderSummaryBlock()}

            {/* <p className="promo-form-v1">
                <LinkV1 {...{
                    href: '#',
                    label: 'Ввести промокод',
                    classes: [
                        'link-v1_text-color-type-main',
                        'link-v1_decoration-type-underline',
                    ]
                }} />
            </p> */}

            {isReadonlyMode === false && (
                <>
                    <div className="action-group-v1">
                        <CartSubmitButtonV1 {...{
                            href: '#',
                            label: 'Оформить заказ',
                            classes: [
                                'button-v1_text-color-type-white',
                                'button-v1_filling-color-type-main',
                                'button-v1_filling-type-contained',
                            ]
                        }} />
                    </div>

                    <p className="agreement-v1">
                        Нажимая на кнопку, вы соглашаетесь на&nbsp;
                        <LinkV1 {...{
                            href: '#',
                            label: 'обработку своих персональных данных',
                            classes: [
                                'link-v1_text-color-type-main',
                                'link-v1_decoration-type-underline',
                            ]
                        }} />
                        &nbsp;и принимаете условия&nbsp;
                        <LinkV1 {...{
                            href: '#',
                            label: 'публичного договора-оферты.',
                            classes: [
                                'link-v1_text-color-type-main',
                                'link-v1_decoration-type-underline',
                            ]
                        }} />
                    </p>
                </>
            )}

            <div className="wave-divider-v1"></div>

        </div>
    );

})

export default CartSummaryBlockV1;